import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Login from './pages/Login';
import Signup from './pages/Signup';
import Dashboard from './pages/Dashboard';
import PatientForm from './pages/PatientForm';
import PatientDashboard from './pages/PatientDashboard';
import VitalsForm from './pages/VitalsForm';
import DocumentUpload from './pages/DocumentUpload';
import Profile from './pages/Profile';
import Settings from './pages/Settings';
import Navbar from './components/Navbar';
import AdminRoute from './components/AdminRoute';
import AdminLogin from './pages/admin/AdminLogin';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminUsers from './pages/admin/AdminUsers';
import AdminInterpretations from './pages/admin/AdminInterpretations';
import AdminUnmatched from './pages/admin/AdminUnmatched';
import AdminSettings from './pages/admin/AdminSettings';
import AdminAIMonitor from './pages/admin/AdminAIMonitor';
import AdminAliases from './pages/admin/AdminAliases';
import './App.css';

// Read ghost_mode from localStorage safely
function readGhostMode() {
  try { return JSON.parse(localStorage.getItem('ghost_mode') || 'null'); } catch { return null; }
}

function GhostBanner() {
  const [ghost, setGhost] = useState(null);

  useEffect(() => {
    const g = readGhostMode();
    if (!g?.active || !g?.token) return;
    if (g.expires_at && new Date(g.expires_at) < new Date()) {
      exitGhost();
      return;
    }
    setGhost(g);
  }, []);

  function exitGhost() {
    const g = readGhostMode();
    localStorage.removeItem('ghost_mode');
    // Restore the previous regular session (if any); otherwise clear it
    if (g?.prev_token) {
      localStorage.setItem('token', g.prev_token);
      localStorage.setItem('user', g.prev_user || '');
      if (g.prev_refresh) localStorage.setItem('refresh_token', g.prev_refresh);
    } else {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('refresh_token');
    }
    window.close();
    setTimeout(() => { window.location.href = '/login'; }, 400);
  }

  if (!ghost) return null;

  const expiresIn = ghost.expires_at
    ? Math.max(0, Math.round((new Date(ghost.expires_at) - new Date()) / 60000))
    : null;

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999,
      background: '#1a202c', color: '#ecc94b',
      padding: '8px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      fontSize: '0.82rem', fontWeight: '700', boxShadow: '0 2px 10px rgba(0,0,0,0.3)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <span style={{ fontSize: '1rem' }}>👁</span>
        <span>Ghost Mode — viewing as <span style={{ color: 'white' }}>{ghost.as_user}</span></span>
        {expiresIn !== null && (
          <span style={{ color: '#a0aec0', fontSize: '0.74rem', fontWeight: '600' }}>({expiresIn}m left)</span>
        )}
      </div>
      <button onClick={exitGhost} style={{
        background: '#e53e3e', color: 'white', border: 'none', borderRadius: '6px',
        padding: '4px 14px', fontWeight: '700', fontSize: '0.78rem', cursor: 'pointer',
      }}>
        Exit Ghost Mode
      </button>
    </div>
  );
}

function App() {
  const [user, setUser]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [ghostActive, setGhostActive] = useState(false);

  useEffect(() => {
    // Check ghost mode — banner shows when active, user identity comes from ghost or regular token
    const ghost = readGhostMode();
    if (ghost?.active) {
      const expired = ghost.expires_at && new Date(ghost.expires_at) < new Date();
      if (expired) {
        localStorage.removeItem('ghost_mode');
      } else {
        setGhostActive(true);
        // Ghost identity comes from ghost_mode.user OR fallback to regular localStorage.user
        const ghostUser = ghost.user || (() => {
          try { return JSON.parse(localStorage.getItem('user')); } catch { return null; }
        })();
        if (ghostUser) { setUser(ghostUser); setLoading(false); return; }
      }
    }

    // Normal session (also handles old-cached-JS ghost path where token=ghost_token)
    const token    = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    if (token && userData) {
      try { setUser(JSON.parse(userData)); } catch {}
    }
    setLoading(false);
  }, []);

  const handleLogin = (data) => {
    localStorage.setItem('token', data.access_token);
    localStorage.setItem('refresh_token', data.refresh_token);
    localStorage.setItem('user', JSON.stringify(data.user));
    setUser(data.user);

    const userData = data.user;
    if (userData.is_google_user && (!userData.phone || !userData.date_of_birth || !userData.gender)) {
      window.location.href = '/profile?incomplete=true';
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    setUser(null);
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <Router>
      <div className="App" style={ghostActive ? { paddingTop: '38px' } : {}}>
        {ghostActive && <GhostBanner />}
        <Routes>
          {/* ── User routes ── */}
          <Route path="/login"  element={user ? <Navigate to="/dashboard" /> : <Login onLogin={handleLogin} />} />
          <Route path="/signup" element={user ? <Navigate to="/dashboard" /> : <Signup onLogin={handleLogin} />} />
          <Route path="/dashboard" element={user ? <Dashboard user={user} /> : <Navigate to="/login" />} />
          <Route path="/patients/new" element={user ? <PatientForm user={user} /> : <Navigate to="/login" />} />
          <Route path="/patients/:patientId" element={user ? <PatientDashboard user={user} /> : <Navigate to="/login" />} />
          <Route path="/patients/:patientId/edit" element={user ? <PatientForm user={user} /> : <Navigate to="/login" />} />
          <Route path="/patients/:patientId/vitals/new" element={user ? <VitalsForm user={user} /> : <Navigate to="/login" />} />
          <Route path="/patients/:patientId/documents/new" element={user ? <DocumentUpload user={user} /> : <Navigate to="/login" />} />
          <Route path="/profile" element={user ? <Profile user={user} /> : <Navigate to="/login" />} />
          <Route path="/settings" element={user ? <Settings user={user} /> : <Navigate to="/login" />} />

          {/* ── Admin routes ── */}
          <Route path="/admin" element={<Navigate to="/admin/login" replace />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin/dashboard" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
          <Route path="/admin/users" element={<AdminRoute><AdminUsers /></AdminRoute>} />
          <Route path="/admin/interpretations" element={<AdminRoute><AdminInterpretations /></AdminRoute>} />
          <Route path="/admin/unmatched" element={<AdminRoute><AdminUnmatched /></AdminRoute>} />
          <Route path="/admin/settings" element={<AdminRoute><AdminSettings /></AdminRoute>} />
          <Route path="/admin/ai" element={<AdminRoute><AdminAIMonitor /></AdminRoute>} />
          <Route path="/admin/aliases" element={<AdminRoute><AdminAliases /></AdminRoute>} />

          <Route path="/" element={<Navigate to={user ? "/dashboard" : "/login"} />} />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
